import { ArticleType } from '@wix/answers-api';
import { Box, Text, TextSize } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import { useTranslation } from '@wix/wix-i18n-config';
import { Article, ChevronRight } from '@wix/wix-ui-icons-common';
import { To } from 'history';
import React, { FC, memo, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import { classNames } from '../../../../util';
import { logSearchResultsAction } from '../../../bi';
import { SearchArticlesItem } from '../../../common/api/searchArticles';
import { TRootState } from '../../../common/reducers';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import { selectNormalizedCategories } from '../../../common/selectors/helpCenter';
import { appRoutes } from '../../../routes';
import { SearchResultItem, Theme } from '../../shared-components/ArticleSearchResultItem';
import s from './styles.scss';

type InteractionLogger = (
  state: TRootState,
  data: {
    id: string;
    distance?: number;
    position: number;
    categoryId?: string;
  },
) => void;

type SearchResultsItemProps = {
  id: string;
  categoryId?: string;
  distance?: number;
  title: string;
  description: string;
  type: ArticleType;
  readingTimeInMinutes: number;
  rowNumber: number;
  interactionLogger: InteractionLogger;
};

export type ArticlesListItemProps = {
  id: string;
  distance?: number;
  title: string;
  titleKey?: string;
  rowNumber: number;
  interactionLogger: InteractionLogger;
  categoryId?: string;
  textSize?: TextSize;
  smallPaddings?: boolean;
  withChevronRight?: boolean;
  ellipsis?: boolean;
  dataHook?: string;
};

export const getLinkPath = (articleId: string, searchParams: URLSearchParams): To => {
  searchParams.delete('query');
  return {
    pathname: appRoutes.articleById({ articleId }),
    search: searchParams.toString(),
  };
};

// TODO: Add support for other types of solutions
export const ArticlesListItem: FC<ArticlesListItemProps> = memo(function ArticlesListItemComponent({
  id,
  distance,
  title,
  titleKey,
  rowNumber,
  interactionLogger,
  categoryId,
  textSize = 'medium',
  smallPaddings = false,
  withChevronRight = false,
  ellipsis = false,
  dataHook = 'articles-list__item',
}) {
  const store = useStore();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const handleArticleClick = useCallback(() => {
    const state = store.getState();
    dispatch(helpCenterSlice.actions.setCurrentArticle({ id, title, categoryId }));
    interactionLogger(state, {
      id,
      categoryId,
      distance,
      position: rowNumber,
    });
  }, [store, dispatch, id, title, categoryId, interactionLogger, distance, rowNumber]);

  const className = classNames([
    s.articlesListItem,
    smallPaddings && s.articlesListItemSmall,
    rowNumber === 0 && s.articlesListItemFirstItem,
    withChevronRight && s.articlesListItemWithChevron,
  ]);

  return (
    <Link
      className={className}
      to={getLinkPath(id, searchParams)}
      onClick={handleArticleClick}
      data-hook={dataHook}
    >
      <Box color="B10">
        <Article className={s.articlesListItemIcon} size="24" />
      </Box>
      <Text
        size={textSize}
        ellipsis={ellipsis}
        tooltipProps={{
          size: 'small',
          maxWidth: '80%',
        }}
        dataHook="articles-list-item"
      >
        {titleKey ? t(titleKey) : title}
      </Text>
      {withChevronRight && <ChevronRight className={s.chevronRightIcon} size="24" />}
    </Link>
  );
});

export const SearchResultItemWrapper: FC<SearchResultsItemProps> = ({
  id,
  categoryId,
  distance,
  title,
  description,
  type,
  readingTimeInMinutes,
  rowNumber,
  interactionLogger,
}) => {
  const store = useStore();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const normalizedCategories = useSelector(selectNormalizedCategories);

  const handleArticleClick = useCallback(() => {
    const state = store.getState();
    dispatch(helpCenterSlice.actions.setCurrentArticle({ id, title, categoryId }));
    interactionLogger(state, {
      id,
      distance,
      position: rowNumber,
    });
  }, [store, dispatch, id, title, categoryId, interactionLogger, distance, rowNumber]);

  const articleLinkProps = {
    to: getLinkPath(id, searchParams),
  };

  const badgeLinkProps = {
    to: appRoutes.categoryById({ categoryId: categoryId! }),
  };

  const sanitizedDescription = sanitizeHtml(description, { stripIgnoreTag: true, whiteList: {} });

  return (
    <SearchResultItem
      id={id}
      distance={distance}
      title={title}
      sanitizedDescription={sanitizedDescription}
      articleType={type}
      readingTimeInMinutes={readingTimeInMinutes}
      rootTopic={normalizedCategories[categoryId!]}
      onArticleTopicBadgeClick={() => {}}
      onArticleClick={handleArticleClick}
      onArticleTypeTagClick={() => {}}
      LinkComponent={Link}
      articleLinkProps={articleLinkProps}
      badgeLinkProps={badgeLinkProps}
      theme={Theme.Widget}
    />
  );
};

export const sharedSearchResultsItem = (
  { id, categoryId, distance, title, description, type, readingTimeInMinutes }: SearchArticlesItem,
  rowNumber: number,
) => {
  return (
    <SearchResultItemWrapper
      id={id}
      categoryId={categoryId}
      distance={distance}
      title={title}
      description={description!}
      type={type!}
      readingTimeInMinutes={readingTimeInMinutes!}
      rowNumber={rowNumber}
      interactionLogger={logSearchResultsAction}
    />
  );
};

// TODO: Remove after test specs.helpwidget.sharedSearchResultsItem is merged.
export const renderArticlesListItem = ({ id, distance, title }: SearchArticlesItem, rowNumber: number) => (
  <ArticlesListItem
    id={id}
    distance={distance}
    title={title}
    rowNumber={rowNumber}
    interactionLogger={logSearchResultsAction}
  />
);
